/**
 * PDP collapsible rows: animate panel open/close with measured max-height + opacity.
 * Theme default uses grid-template-rows (feels snappy). We patch collapsible-section
 * toggles so open/close use:
 *   transition: max-height 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)
 */
(function () {
  var EASE = 'cubic-bezier(0, 0, 0.2, 1)';
  var MS = 300;
  var TRANS =
    'max-height ' + MS + 'ms ' + EASE + ', opacity ' + MS + 'ms ' + EASE;

  function isPdpCollapse(el) {
    return (
      el &&
      el.classList &&
      el.classList.contains('wt-collapse--always') &&
      el.closest &&
      el.closest('.wt-product')
    );
  }

  function getTarget(section) {
    return (
      (section && section.content) ||
      (section &&
        section.querySelector &&
        section.querySelector('.wt-collapse__target'))
    );
  }

  function isTriggerActive(section) {
    return !!(
      section &&
      section.trigger &&
      section.trigger.classList.contains('wt-collapse__trigger--active')
    );
  }

  function clearEndHandlers(target) {
    if (target._amCollapseFallback) {
      clearTimeout(target._amCollapseFallback);
      target._amCollapseFallback = null;
    }
    if (target._amCollapseEnd) {
      target.removeEventListener('transitionend', target._amCollapseEnd);
      target._amCollapseEnd = null;
    }
  }

  function prepareTarget(target) {
    target.style.display = 'block';
    target.style.overflow = 'hidden';
    target.style.gridTemplateRows = 'unset';
  }

  function animateOpen(target) {
    clearEndHandlers(target);
    prepareTarget(target);
    target.style.transition = 'none';
    target.style.maxHeight = '0px';
    target.style.opacity = '0';
    void target.offsetHeight;
    target.style.transition = TRANS;
    target.style.maxHeight = target.scrollHeight + 'px';
    target.style.opacity = '1';

    var doneOpen = function (e) {
      if (e && e.propertyName && e.propertyName !== 'max-height') return;
      clearEndHandlers(target);
      var trigger = target.previousElementSibling;
      if (
        trigger &&
        trigger.classList.contains('wt-collapse__trigger--active')
      ) {
        target.style.maxHeight = 'none';
      }
    };
    target._amCollapseEnd = doneOpen;
    target._amCollapseFallback = setTimeout(doneOpen, MS + 40);
    target.addEventListener('transitionend', doneOpen);
  }

  function animateClose(target) {
    clearEndHandlers(target);
    prepareTarget(target);
    target.style.transition = TRANS;
    void target.offsetHeight;
    target.style.maxHeight = '0px';
    target.style.opacity = '0';
  }

  /** Freeze open height before the trigger loses --active (CSS would collapse first). */
  function freezeOpenHeight(target) {
    if (!target) return;
    clearEndHandlers(target);
    prepareTarget(target);
    target.style.transition = 'none';
    target.style.maxHeight = target.scrollHeight + 'px';
    target.style.opacity = '1';
    void target.offsetHeight;
  }

  /**
   * Claude QA 10-7: the product info column is sticky and can be taller than the screen, so an
   * opened row (Details, Care) used to sit below the fold until you scrolled past the gallery.
   * The column now scrolls on its own (see am-pdp-collapsible.css); after a row opens, bring it into view.
   */
  function revealOpened(section) {
    var scroller = section && section.closest && section.closest('.wt-product__main');
    if (!scroller) return;
    setTimeout(function () {
      var box = scroller.getBoundingClientRect();
      var row = section.getBoundingClientRect();
      if (scroller.scrollHeight > scroller.clientHeight + 1) {
        if (row.bottom > box.bottom) {
          scroller.scrollBy({ top: Math.min(row.bottom - box.bottom + 16, row.top - box.top), behavior: 'smooth' });
        }
      } else if (row.bottom > window.innerHeight) {
        window.scrollBy({ top: Math.min(row.bottom - window.innerHeight + 16, row.top - 80), behavior: 'smooth' });
      }
    }, 320);
  }

  function patch() {
    var Ctor = customElements.get('collapsible-section');
    if (!Ctor || Ctor.prototype.__amPdpCollapseAnim) return;
    Ctor.prototype.__amPdpCollapseAnim = true;

    var origToggleState = Ctor.prototype.toggleState;
    Ctor.prototype.toggleState = function () {
      if (!isPdpCollapse(this)) {
        origToggleState.call(this);
        return;
      }

      var target = getTarget(this);
      var wasActive = isTriggerActive(this);
      if (wasActive) freezeOpenHeight(target);

      origToggleState.call(this);

      if (isTriggerActive(this)) {
        animateOpen(target);
        revealOpened(this);
      } else animateClose(target);
    };

    var origToggleAccordion = Ctor.prototype.toggleAccordion;
    Ctor.prototype.toggleAccordion = function () {
      if (!isPdpCollapse(this)) {
        origToggleAccordion.call(this);
        return;
      }

      var self = this;
      var accordionPanels = document.querySelectorAll(
        'collapsible-section[data-accordion-set="' + this.accordionSet + '"]'
      );

      if (isTriggerActive(this) || this.isOpen()) {
        this.toggleState();
        return;
      }

      accordionPanels.forEach(function (panelEl) {
        if (panelEl === self) return;
        var wasOpen =
          (panelEl.trigger &&
            panelEl.trigger.classList.contains(
              'wt-collapse__trigger--active'
            )) ||
          (panelEl.isOpen && panelEl.isOpen());
        if (wasOpen) freezeOpenHeight(getTarget(panelEl));
        panelEl.dataset.open = 'false';
        var panelTrigger = panelEl.querySelector('.wt-collapse__trigger');
        if (panelTrigger) {
          panelTrigger.classList.remove('wt-collapse__trigger--active');
        }
        if (wasOpen) animateClose(getTarget(panelEl));
      });

      this.dataset.open = 'true';
      if (this.trigger) {
        this.trigger.classList.add('wt-collapse__trigger--active');
      }
      animateOpen(getTarget(this));
      revealOpened(this);
    };
  }

  if (customElements.get('collapsible-section')) {
    patch();
  } else if (customElements.whenDefined) {
    customElements.whenDefined('collapsible-section').then(patch);
  }

  /** Ensure PDP rows start closed (ignore theme "open by default" settings). */
  function forceClosedOnLoad() {
    document
      .querySelectorAll('.wt-product collapsible-section.wt-collapse--always')
      .forEach(function (section) {
        section.dataset.open = 'false';
        var trigger =
          section.trigger || section.querySelector('.wt-collapse__trigger');
        var target = getTarget(section);
        if (trigger) {
          trigger.classList.remove('wt-collapse__trigger--active');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('data-open', 'false');
        }
        if (target) {
          clearEndHandlers(target);
          prepareTarget(target);
          target.style.transition = 'none';
          target.style.maxHeight = '0px';
          target.style.opacity = '0';
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', forceClosedOnLoad);
  } else {
    forceClosedOnLoad();
  }
})();
